<template>
  <div class="app-container">
    <div class="resizable-layout">
      <!-- 左侧树形组件 -->
      <div class="tree-panel" :style="{ width: treeWidth + 'px' }">
        <room-tree-div ref="roomTreeDiv" @selectRoom="handleSelectRoom"></room-tree-div>
      </div>

      <!-- 拖拽分隔线 -->
      <div class="resize-handle" @mousedown="startResize" @mouseover="showResizeCursor" @mouseleave="hideResizeCursor">
        <div class="resize-line"></div>
      </div>

      <!-- 右侧内容区域 -->
      <div class="content-panel" :style="{ width: 'calc(100% - ' + (treeWidth + 10) + 'px)' }">
        <div class="box-card">
          <!-- (1) PageHeader -->
          <ds-page-header class="box-card__header" :title="roomCreateFeeInfo.roomName"
            :subtitle="$t('roomCreateFee.page.subtitle')">
            <template #extra>
              <el-button v-if="hasPrivilege('502022073096990007')" size="small" icon="el-icon-download"
                @click="_openFeeImportExcel">
                {{ $t('roomCreateFee.customTemplate') }}
              </el-button>
              <el-button v-if="hasPrivilege('502022073096990007')" size="small" icon="el-icon-plus"
                @click="_openDoCreateRoomFee">
                {{ $t('roomCreateFee.customImport') }}
              </el-button>
              <el-button v-if="hasPrivilege('502022073096990007')" size="small" icon="el-icon-plus"
                @click="_toBatchCreateFee(null, true)">
                {{ $t('roomCreateFee.batchCreate') }}
              </el-button>
              <el-button size="small" icon="el-icon-printer" @click="_downloadCollectionLetterOrder">
                {{ $t('roomCreateFee.batchCollectionLetter') }}
              </el-button>
            </template>
          </ds-page-header>

          <!-- (2) SearchPanel -->
          <ds-search-panel class="box-card__body">
            <el-row :gutter="16">
              <el-col :xs="24" :sm="12" :md="8" :lg="6">
                <el-input v-model="roomCreateFeeInfo.condition.roomNum"
                  :placeholder="$t('roomCreateFee.roomNumPlaceholder')" clearable
                  @blur="listRoomInRoomCreateFee"></el-input>
                <input-search-room ref="inputSearchRoom"></input-search-room>
              </el-col>
              <el-col :xs="24" :sm="12" :md="8" :lg="6">
                <el-input v-model="roomCreateFeeInfo.condition.ownerName"
                  :placeholder="$t('roomCreateFee.ownerNamePlaceholder')" clearable
                  @input="_inputRoomByOwner"></el-input>
                <input-search-room-by-owner ref="inputSearchRoomByOwner"></input-search-room-by-owner>
              </el-col>
            </el-row>

            <template #actions>
              <el-button type="primary" icon="el-icon-search" @click="_queryRoomCreateFeeMethod">
                {{ $t('common.query') }}
              </el-button>
              <el-button icon="el-icon-refresh-left" @click="_resetRoomCreateFeeMethod">
                {{ $t('common.reset') }}
              </el-button>
            </template>
          </ds-search-panel>

          <!-- Tab 区：房屋费用 / 缴费历史 / 房屋 / 业主 / 催缴 / 补打收据 等 -->
          <div class="box-card__tabs">
            <el-tabs v-model="roomCreateFeeInfo._currentTab" @tab-click="changeTab(roomCreateFeeInfo._currentTab)">
              <el-tab-pane :label="$t('roomCreateFee.roomFee')" name="simplifyRoomFee">
                <simplify-room-fee v-if="roomCreateFeeInfo._currentTab === 'simplifyRoomFee'" ref="simplifyRoomFee"
                  :key="'simplifyRoomFee' + roomCreateFeeInfo.roomId"></simplify-room-fee>
              </el-tab-pane>
              <el-tab-pane v-if="roomCreateFeeInfo.ownerId" :label="$t('roomCreateFee.paymentHistory')"
                name="simplifyHisFee">
                <simplify-his-fee v-if="roomCreateFeeInfo._currentTab === 'simplifyHisFee'" ref="simplifyHisFee"
                  :key="'simplifyHisFee' + roomCreateFeeInfo.roomId"></simplify-his-fee>
              </el-tab-pane>
              <el-tab-pane v-if="roomCreateFeeInfo.ownerId" :label="$t('roomCreateFee.rooms')" name="simplifyOwnerRooms">
                <simplify-owner-rooms v-if="roomCreateFeeInfo._currentTab === 'simplifyOwnerRooms'"
                  ref="simplifyOwnerRooms" :key="'simplifyOwnerRooms' + roomCreateFeeInfo.roomId" hasChange="N"></simplify-owner-rooms>
              </el-tab-pane>
              <el-tab-pane v-if="roomCreateFeeInfo.ownerId" :label="$t('roomCreateFee.owner')" name="carDetailOwner">
                <car-detail-owner v-if="roomCreateFeeInfo._currentTab === 'carDetailOwner'" ref="carDetailOwner"
                  :key="'carDetailOwner' + roomCreateFeeInfo.roomId"></car-detail-owner>
              </el-tab-pane>
              <el-tab-pane :label="$t('roomCreateFee.callable')" name="simplifyCallable">
                <simplify-callable v-if="roomCreateFeeInfo._currentTab === 'simplifyCallable'" ref="simplifyCallable"
                  :key="'simplifyCallable' + roomCreateFeeInfo.roomId"></simplify-callable>
              </el-tab-pane>
              <el-tab-pane :label="$t('roomCreateFee.receiptReprint')" v-if="hasPrivilege('502020092373407363')"
                name="simplifyFeeReceipt">
                <simplify-fee-receipt v-if="roomCreateFeeInfo._currentTab === 'simplifyFeeReceipt'"
                  ref="simplifyFeeReceipt" :key="'simplifyFeeReceipt' + roomCreateFeeInfo.roomId"></simplify-fee-receipt>
              </el-tab-pane>
              <el-tab-pane :label="$t('roomCreateFee.meterReadingRecords')" name="simplifyMeterWaterLog">
                <simplify-meter-water-log v-if="roomCreateFeeInfo._currentTab === 'simplifyMeterWaterLog'"
                  ref="simplifyMeterWaterLog"
                  :key="'simplifyMeterWaterLog' + roomCreateFeeInfo.roomId"></simplify-meter-water-log>
              </el-tab-pane>
              <el-tab-pane v-if="roomCreateFeeInfo.ownerId" :label="$t('roomCreateFee.prepaidAccount')"
                name="ownerDetailAccount">
                <owner-detail-account v-if="roomCreateFeeInfo._currentTab === 'ownerDetailAccount'"
                  ref="ownerDetailAccount" :key="'ownerDetailAccount' + roomCreateFeeInfo.roomId"></owner-detail-account>
              </el-tab-pane>
              <el-tab-pane v-if="roomCreateFeeInfo.ownerId" :label="$t('roomCreateFee.prepaidReprint')"
                name="ownerDetailAccountReceipt">
                <owner-detail-account-receipt v-if="roomCreateFeeInfo._currentTab === 'ownerDetailAccountReceipt'"
                  ref="ownerDetailAccountReceipt"
                  :key="'ownerDetailAccountReceipt' + roomCreateFeeInfo.roomId"></owner-detail-account-receipt>
              </el-tab-pane>
              <el-tab-pane :label="$t('roomCreateFee.deposit')" name="simplifyRefundDeposit">
                <simplify-refund-deposit v-if="roomCreateFeeInfo._currentTab === 'simplifyRefundDeposit'"
                  ref="simplifyRefundDeposit"
                  :key="'simplifyRefundDeposit' + roomCreateFeeInfo.roomId"></simplify-refund-deposit>
              </el-tab-pane>
              <!-- <el-tab-pane :label="$t('roomCreateFee.billingRules')" name="feeDetailFeeRule">
                <fee-detail-fee-rule v-if="roomCreateFeeInfo._currentTab === 'feeDetailFeeRule'" ref="feeDetailFeeRule"
                  :key="'feeDetailFeeRule' + roomCreateFeeInfo.roomId"></fee-detail-fee-rule>
              </el-tab-pane>
              <el-tab-pane :label="$t('roomCreateFee.feeBills')" name="feeDetailRuleBill">
                <fee-detail-rule-bill v-if="roomCreateFeeInfo._currentTab === 'feeDetailRuleBill'" ref="feeDetailRuleBill"
                  :key="'feeDetailRuleBill' + roomCreateFeeInfo.roomId"></fee-detail-rule-bill>
              </el-tab-pane> -->
            </el-tabs>
          </div>
        </div>
      </div>
    </div>

    <export-fee-import-excel ref="exportFeeImportExcel"></export-fee-import-excel>
    <do-import-create-fee ref="doImportCreateFee"></do-import-create-fee>
    <download-collection-letter-order ref="downloadCollectionLetterOrder"></download-collection-letter-order>
  </div>
</template>

<script>
import RoomTreeDiv from '@/components/room/roomTreeDiv'
import InputSearchRoom from '@/components/fee/inputSearchRoom'
import InputSearchRoomByOwner from '@/components/fee/inputSearchRoomByOwner'
import SimplifyRoomFee from '@/components/fee/simplifyRoomFee'
import SimplifyHisFee from '@/components/fee/simplifyHisFee'
import SimplifyOwnerRooms from '@/components/fee/simplifyOwnerRooms'
import CarDetailOwner from '@/components/fee/carDetailOwner'
import SimplifyCallable from '@/components/fee/simplifyCallable'
import SimplifyFeeReceipt from '@/components/fee/simplifyFeeReceipt'
import SimplifyMeterWaterLog from '@/components/fee/simplifyMeterWaterLog'
import OwnerDetailAccount from '@/components/owner/ownerDetailAccount'
import OwnerDetailAccountReceipt from '@/components/owner/ownerDetailAccountReceipt'
import SimplifyRefundDeposit from '@/components/fee/simplifyRefundDeposit'
// import FeeDetailFeeRule from '@/components/fee/feeDetailFeeRule'
// import FeeDetailRuleBill from '@/components/fee/feeDetailRuleBill'

import ExportFeeImportExcel from '@/components/fee/exportFeeImportExcel'
import DoImportCreateFee from '@/components/fee/doImportCreateFee'
import DownloadCollectionLetterOrder from '@/components/fee/downloadCollectionLetterOrder'
import { getCommunityId } from '@/api/community/communityApi'
import { queryRooms } from '@/api/fee/roomCreateFeeApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'

export default {
  name: 'RoomCreateFeeList',
  components: {
    RoomTreeDiv,
    InputSearchRoom,
    InputSearchRoomByOwner,
    SimplifyRoomFee,
    SimplifyHisFee,
    SimplifyOwnerRooms,
    CarDetailOwner,
    SimplifyCallable,
    SimplifyFeeReceipt,
    SimplifyMeterWaterLog,
    OwnerDetailAccount,
    OwnerDetailAccountReceipt,
    SimplifyRefundDeposit,
    // FeeDetailFeeRule,
    // FeeDetailRuleBill,

    ExportFeeImportExcel,
    DoImportCreateFee,
    DownloadCollectionLetterOrder,
    DsPageHeader,
    DsSearchPanel
  },
  data() {
    return {
      roomCreateFeeInfo: {
        roomName: '',
        roomId: '',
        ownerId: '',
        ownerName: '',
        roomType: '',
        _currentTab: 'simplifyRoomFee',
        condition: {
          roomNum: '',
          ownerName: ''
        },
        timer: null
      },
      communityId: '',
      // 新增：拖拽相关数据
      treeWidth: 250, // 默认树形组件宽度
      minTreeWidth: 200, // 最小宽度
      maxTreeWidth: 600, // 最大宽度
      isResizing: false,
      startX: 0,
      startWidth: 0
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.initRoomTree()
    // 恢复保存的宽度
    this.restoreTreeWidth()
  },
  mounted() {
    // 添加全局鼠标事件监听
    document.addEventListener('mousemove', this.onMouseMove)
    document.addEventListener('mouseup', this.onMouseUp)
  },
  beforeDestroy() {
    // 移除全局事件监听
    document.removeEventListener('mousemove', this.onMouseMove)
    document.removeEventListener('mouseup', this.onMouseUp)
  },
  methods: {
    initRoomTree() {
      // this.$refs.roomTreeDiv.initRoomTreeDiv({
      //   callName: 'roomCreateFee'
      // })
    },
    handleSelectRoom(param) {
      this.roomCreateFeeInfo.ownerName = ''
      this.roomCreateFeeInfo.ownerId = ''
      this.roomCreateFeeInfo.roomId = param.roomId
      this.roomCreateFeeInfo.roomName = param.roomName

      this.roomCreateFeeInfo.condition.ownerName = ''
      this.roomCreateFeeInfo.condition.roomNum = ''
      this.listRoomInRoomCreateFee()
    },
    notifyRoomByOwner(room) {
      this.$set(this.roomCreateFeeInfo, 'ownerName', room.ownerName)
      this.$set(this.roomCreateFeeInfo, 'ownerId', room.ownerId)
      this.$set(this.roomCreateFeeInfo, 'roomId', room.roomId)
      this.$set(
        this.roomCreateFeeInfo,
        'roomName',
        `${room.floorNum}-${room.unitNum}-${room.roomNum}`
      )
      this.listRoomInRoomCreateFee()
    },
    async listRoomInRoomCreateFee() {
      const param = {
        page: 1,
        row: 1,
        communityId: this.communityId,
        roomId: this.roomCreateFeeInfo.roomId
      }

      if (this.roomCreateFeeInfo.condition.roomNum) {
        param.flag = '1'
        param.roomNum = this.roomCreateFeeInfo.condition.roomNum
        param.roomId = ''
        this.roomCreateFeeInfo.roomName = ''
        this.roomCreateFeeInfo.ownerName = ''
        this.roomCreateFeeInfo.ownerId = ''
        this.roomCreateFeeInfo.roomId = ''
      }

      try {
        const response = await queryRooms(param)
        const room = response.rooms[0]
        Object.assign(this.roomCreateFeeInfo, room)
        this.changeTab('simplifyRoomFee')
      } catch (error) {
        console.error('Request failed:', error)
      }
    },
    _inputRoomByOwner() {
      if (this.roomCreateFeeInfo.timer) {
        clearTimeout(this.roomCreateFeeInfo.timer)
      }
      this.roomCreateFeeInfo.timer = setTimeout(() => {
        this.$refs.inputSearchRoomByOwner.$emit('searchRoom', {
          callComponent: 'roomCreateFee',
          ownerName: this.roomCreateFeeInfo.condition.ownerName
        })
      }, 1500)
    },
    _queryRoomCreateFeeMethod() {
      this.listRoomInRoomCreateFee()
    },
    _resetRoomCreateFeeMethod() {
      this.$refs.inputSearchRoom.$emit('close', {})
      this.$refs.inputSearchRoomByOwner.$emit('close', {})
      this.roomCreateFeeInfo.roomNum = ''
      this.roomCreateFeeInfo.ownerName = ''
      this.roomCreateFeeInfo.ownerId = ''
      this.roomCreateFeeInfo.condition.ownerName = ''
      this.roomCreateFeeInfo.condition.roomNum = ''
      this.changeTab('simplifyRoomFee')
    },
    changeTab(tab) {
      this.roomCreateFeeInfo._currentTab = tab
      setTimeout(() => {
        if (this.$refs[tab]) {
          if (tab === 'ownerDetailAccount' || tab === 'ownerDetailAccountReceipt') {
            this.$refs[tab].open(this.roomCreateFeeInfo.ownerId, this.roomCreateFeeInfo.ownerName, this.roomCreateFeeInfo.roomId)
            return;
          }
          this.$refs[tab].open({
            ownerId: this.roomCreateFeeInfo.ownerId,
            ownerName: this.roomCreateFeeInfo.ownerName,
            roomId: this.roomCreateFeeInfo.roomId,
            roomName: this.roomCreateFeeInfo.roomName
          })
        }
      }, 500)
    },
    _roomCreateFeetoSimplifyAcceptance() {

      this.$router.push('/pages/property/simplifyAcceptance?tab=业务受理&searchType=1&searchValue=' + `${this.roomCreateFeeInfo.floorNum}-${this.roomCreateFeeInfo.unitNum}-${this.roomCreateFeeInfo.roomNum}`)

    },
    _toBatchCreateFee() {
      this.$router.push('/pages/fee/roomCreatePayFee')
    },
    _downloadCollectionLetterOrder() {
      this.$router.push('/views/fee/addOweFeeCallable?callableWay=EXCEL')
    },
    _downloadRoomCollectionLetterOrder(room) {
      this.$refs.downloadCollectionLetterOrder.open({
        roomId: room.roomId
      })
    },
    _openFeeImportExcel() {
      this.$refs.exportFeeImportExcel.open()
    },
    _openDoCreateRoomFee() {
      this.$refs.doImportCreateFee.open()
    },
    _openRoomCreateFeeComboModal() {
      this.$router.push(
        `/pages/property/createFeeByCombo?payerObjId=${this.roomCreateFeeInfo.roomId}&payerObjName=${this.roomCreateFeeInfo.roomName}&payerObjType=3333`
      )
    },
    restoreTreeWidth() {
      const savedWidth = localStorage.getItem('roomCreateFeeTreeWidth')
      if (savedWidth) {
        const width = parseInt(savedWidth)
        if (width >= this.minTreeWidth && width <= this.maxTreeWidth) {
          this.treeWidth = width
        }
      }
    },
    // 新增：保存树形组件宽度
    saveTreeWidth() {
      localStorage.setItem('roomCreateFeeTreeWidth', this.treeWidth.toString())
    },
    // 新增：开始拖拽调整大小
    startResize(event) {
      event.preventDefault()
      this.isResizing = true
      this.startX = event.clientX
      this.startWidth = this.treeWidth

      // 添加拖拽时的样式
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },
    // 新增：鼠标移动处理
    onMouseMove(event) {
      if (!this.isResizing) return

      const deltaX = event.clientX - this.startX
      const newWidth = this.startWidth + deltaX

      // 限制宽度范围
      if (newWidth >= this.minTreeWidth && newWidth <= this.maxTreeWidth) {
        this.treeWidth = newWidth
      }
    },
    // 新增：鼠标释放处理
    onMouseUp() {
      if (this.isResizing) {
        this.isResizing = false
        this.saveTreeWidth()

        // 恢复鼠标样式
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    },
    // 新增：显示调整大小光标
    showResizeCursor() {
      if (!this.isResizing) {
        document.body.style.cursor = 'col-resize'
      }
    },
    // 新增：隐藏调整大小光标
    hideResizeCursor() {
      if (!this.isResizing) {
        document.body.style.cursor = ''
      }
    },
  }
}
</script>
<style lang="scss" scoped>
.app-container {
	padding: var(--ds-spacing-lg);
	height: calc(100vh - 120px);
	overflow: hidden;
}

.resizable-layout {
	display: flex;
	flex-direction: row;
	height: 100%;
}

.tree-panel {
	display: flex;
	flex-direction: column;
	min-width: 0;
	background: var(--ds-gray-100);
	border-radius: var(--ds-radius-md);
	padding: var(--ds-spacing-sm);
	border: 1px solid var(--ds-gray-300);
	box-shadow: var(--ds-shadow-sm);
	overflow: hidden;
}

.content-panel {
	overflow-y: auto;
	height: 100%;
	padding-right: 2px;
}

.resize-handle {
	width: 10px;
	cursor: col-resize;
	z-index: 10;
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
}

/* hover 时整条高亮，暗示可拖拽 */
.resize-handle::after {
	content: '';
	position: absolute;
	top: 0;
	bottom: 0;
	width: 3px;
	background-color: transparent;
	border-radius: var(--ds-radius-full);
	transition: background-color var(--ds-transition-fast);
}

.resize-handle:hover::after {
	background-color: rgba(var(--ds-primary-rgb), 0.35);
}

.resize-handle:hover .resize-line {
	background-color: var(--ds-primary);
	height: 56px;
}

.resize-line {
	width: 2px;
	height: 40px;
	background-color: var(--ds-gray-300);
	border-radius: var(--ds-radius-sm);
	transition: background-color var(--ds-transition-fast),
		height var(--ds-transition-fast);
}

.resizable-layout.resizing {
	cursor: col-resize;
}

.resizable-layout.resizing .tree-panel,
.resizable-layout.resizing .content-panel {
	pointer-events: none;
}

.resizable-layout.resizing .resize-handle {
	pointer-events: auto;
}

/* 右侧内容面板：与左侧树面板等高、圆角阴影统一 */
.box-card {
	min-height: 100%;
	display: flex;
	flex-direction: column;
	background: #fff;
	border: 1px solid var(--ds-gray-300);
	border-radius: var(--ds-radius-md);
	box-shadow: var(--ds-shadow-sm);
	/* element 默认 overflow:hidden 会让内部 sticky 失效，这里放开 */
	overflow: visible;

	/* 页面标题区 */
	&__header {
		padding: var(--ds-spacing-md) var(--ds-spacing-lg);
		background-color: #fff;
		border-bottom: 1px solid var(--ds-gray-200);
		border-radius: var(--ds-radius-md) var(--ds-radius-md) 0 0;
	}

	/* 筛选区 */
	&__body {
		margin: 0;
		padding: var(--ds-spacing-md) var(--ds-spacing-lg) 0;
		background-color: #fff;
		border: 0;
	}

	/* Tab 区 */
	&__tabs {
		flex: 1;
		padding: 0 var(--ds-spacing-lg) var(--ds-spacing-lg);
		background-color: #fff;
	}

	/* Tabs：吸顶于标题区下方，选中态更清晰 */
	::v-deep .el-tabs__header {
		margin-bottom: var(--ds-spacing-md);
		position: sticky;
		top: 0;
		z-index: 2;
		background-color: #fff;
		padding-top: var(--ds-spacing-xs);
	}

	::v-deep .el-tabs__nav-wrap::after {
		height: 1px;
		background-color: var(--ds-gray-200);
	}

	::v-deep .el-tabs__item {
		color: var(--ds-gray-600);
		font-size: var(--ds-font-size-sm);
		padding: 0 var(--ds-spacing-md);
		line-height: 40px;
	}

	::v-deep .el-tabs__item:hover {
		color: var(--ds-primary);
	}

	::v-deep .el-tabs__item.is-active {
		font-weight: 600;
	}

	::v-deep .el-tabs__active-bar {
		height: 3px;
		border-radius: var(--ds-radius-full);
	}
}

/* 搜索面板内的输入框与按钮统一高度 */
.box-card__body {
	::v-deep .el-input__inner {
		height: 34px;
		line-height: 34px;
	}

	::v-deep .el-button {
		height: 34px;
		border-radius: var(--ds-radius-sm);
	}
}

.tree-panel * {
	user-select: none;
}

.resizable-layout.resizing * {
	user-select: none !important;
}
</style>
